import { FALLBACK_PMT_LANGUAGE_CODE } from '@/constants';
import { useGlobalState } from '@/hooks';
import { translateEventLocation } from '@/modules/pmt/fetchers';
import {
  type ICarrierWithFirstFoundEvent,
  type IParcelEvent,
  type IShipment,
  type ITrackingProgressTracker,
  ShipmentSearchStatus,
  TrackingMilestone,
} from '@/modules/pmt/interfaces';
import {
  HTMLEntitiesConvert,
  buildTranslateEventsObj,
  getShipmentSearchStatus,
  sortEventList,
} from '@/modules/pmt/utils';
import produce from 'immer';
import { useEffect, useMemo, useState } from 'react';

type UseTrackingDetails = (trackingDetails?: IShipment) => {
  carrierList: ICarrierWithFirstFoundEvent[];
  sortedEventList: IParcelEvent[];
  eventCardDetail: {
    event: IParcelEvent | null;
    eventHeaderState: ShipmentSearchStatus;
    progressTrackerState: ITrackingProgressTracker;
  };
};

type EventsById = {
  [eventId: string]: IParcelEvent;
};

const useTrackingDetails: UseTrackingDetails = (trackingDetails) => {
  const { pmtCurrentLanguageCode } = useGlobalState();
  const [eventsById, setEventsById] = useState<EventsById>({});
  const events = useMemo(() => {
    return trackingDetails?.data?.parcel?.events ?? [];
  }, [trackingDetails?.data?.parcel?.events]);
  const rawParcel = trackingDetails?.data?.rawParcel;

  useEffect(() => {
    if (!events.length) {
      setEventsById({});
    } else {
      setEventsById(
        events.reduce<EventsById>((acc, event) => {
          return { ...acc, [event.id]: event };
        }, {}),
      );
    }
  }, [events]);

  const eventHeaderState = getShipmentSearchStatus(trackingDetails?.data);

  useEffect(() => {
    if (
      !rawParcel ||
      !events.some(({ location }) => !!location) ||
      eventHeaderState !== ShipmentSearchStatus.Fetched ||
      pmtCurrentLanguageCode === FALLBACK_PMT_LANGUAGE_CODE
    ) {
      return;
    }

    (async () => {
      const translateObject = buildTranslateEventsObj(rawParcel);

      try {
        const response = await translateEventLocation(pmtCurrentLanguageCode, translateObject);
        if (!response.data?.events?.length) return;

        setEventsById(
          produce((draft) => {
            response.data.events.forEach((event: any) => {
              if (!draft[event.id]?.location) return;
              draft[event.id].location = HTMLEntitiesConvert(event.event_location || '') || null;
            });
          }),
        );
      } catch (err) {
        console.error(err);
      }
    })();
  }, [rawParcel, events, eventHeaderState, pmtCurrentLanguageCode]);

  // Event list (latest sorted)
  const sortedEventList = useMemo(() => {
    return sortEventList(eventsById);
  }, [eventsById]);

  // Carrier list used in above event list (latest sorted)
  const carrierList = useMemo(() => {
    return [...sortedEventList].reverse().reduce<ICarrierWithFirstFoundEvent[]>((foundCarrierList, event) => {
      const isCarrierFound = foundCarrierList.find((carrier) => carrier.id === event.carrier.id);

      if (!isCarrierFound) {
        foundCarrierList.unshift({
          ...event.carrier,
          firstFoundInEvent: event,
        });
      }

      return foundCarrierList;
    }, []);
  }, [sortedEventList]);

  const eventCardDetail = useMemo(() => {
    return {
      event: sortedEventList[0] || null,
      eventHeaderState,
      progressTrackerState: {
        hasIssue: !!trackingDetails?.data?.hasIssue,
        milestone: trackingDetails?.data?.milestone || TrackingMilestone.Pending,
      },
    };
  }, [sortedEventList, eventHeaderState, trackingDetails?.data]);

  return {
    carrierList,
    sortedEventList,
    eventCardDetail,
  };
};

export default useTrackingDetails;
